// Benching (Konzept §7): Polaroid aus dem Framebuffer, kurz zeigen, ins
// persönliche Logbuch. Reine Chronik ohne Nutzen — kein Zähler, keine
// Belohnung, keine Ortsanzeige/Karte (§4, §8).

import { formatClock } from '../../shared/worldtime.js';
import { SEASON_ID } from '../../shared/constants.js';

const STORAGE_KEY = 'th-photo-log';
const MAX_PHOTOS = 30;
const SHOW_S = 4.0;

// ---------------------------------------------------------------------------
// Reine Logbuch-Logik (localStorage-frei testbar)
// ---------------------------------------------------------------------------

/** Neues Foto vorne einfügen, auf max begrenzen. Gibt neues Array zurück. */
export function appendPhotoLog(log, entry, max = MAX_PHOTOS) {
  const next = [{ ...entry }, ...log];
  if (next.length > max) next.length = max;
  return next;
}

/** Alte Einträge bleiben gültig; fehlende Season wird als S1 interpretiert. */
export function normalizePhotoEntry(entry) {
  if (!entry || typeof entry !== 'object' || typeof entry.dataUrl !== 'string') return null;
  const ws = Number.isFinite(entry.ws) ? entry.ws : 0;
  return {
    ws,
    clock: typeof entry.clock === 'string' ? entry.clock : formatClock(ws, false),
    season: typeof entry.season === 'string' && entry.season ? entry.season : 'S1',
    dataUrl: entry.dataUrl,
  };
}

export function loadPhotoLog(storage = null) {
  try {
    const raw = (storage ?? globalThis.localStorage)?.getItem?.(STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    const out = [];
    for (let i = 0; i < data.length && out.length < MAX_PHOTOS; i++) {
      const entry = normalizePhotoEntry(data[i]);
      if (entry) out.push(entry);
    }
    return out;
  } catch {
    return [];
  }
}

export function savePhotoLog(log, storage = null) {
  try {
    (storage ?? globalThis.localStorage)?.setItem?.(
      STORAGE_KEY,
      JSON.stringify(log.slice(0, MAX_PHOTOS)),
    );
  } catch {
    // Quota / privater Modus — Erinnerung verfällt zuerst; Spiel bleibt intakt.
  }
}

// ---------------------------------------------------------------------------
// PhotoSystem
// ---------------------------------------------------------------------------

export class PhotoSystem {
  async init(ctx) {
    this.root = document.getElementById('hud-photo');
    this.log = loadPhotoLog();
    this._showT = 0;
    this._busy = false;
    this._pending = false;
    this.bookOpen = false;

    if (this.root) {
      this.root.textContent = '';
      this.frame = document.createElement('div');
      this.frame.className = 'photo-polaroid';
      this.frame.style.cssText = [
        'display:none',
        'background:#e8e4d9',
        'padding:8px 8px 28px 8px',
        'box-shadow:0 4px 24px rgba(0,0,0,0.55)',
        'transform:rotate(-1.5deg)',
        'max-width:min(42vw, 280px)',
      ].join(';');
      this.img = document.createElement('img');
      this.img.alt = '';
      this.img.style.cssText = 'display:block;width:100%;height:auto;background:#111';
      this.stamp = document.createElement('div');
      this.stamp.style.cssText = [
        'position:absolute',
        'left:0;right:0;bottom:6px',
        'text-align:center',
        'color:#3a3832',
        'font-size:11px',
        'letter-spacing:0.06em',
        'font-family:var(--mono), monospace',
      ].join(';');
      this.frame.style.position = 'relative';
      this.frame.append(this.img, this.stamp);

      this.book = document.createElement('div');
      this.book.className = 'photo-book hidden';
      this.book.setAttribute('role', 'dialog');
      this.book.setAttribute('aria-label', 'Fotochronik');
      this.bookInner = document.createElement('div');
      this.bookInner.className = 'photo-book-inner';
      this.bookTitle = document.createElement('h2');
      this.bookTitle.textContent = 'chronik';
      this.bookGrid = document.createElement('div');
      this.bookGrid.className = 'photo-book-grid';
      this.bookHint = document.createElement('p');
      this.bookHint.textContent = 'B schließen';
      this.bookHint.className = 'photo-book-hint';
      this.bookInner.append(this.bookTitle, this.bookGrid, this.bookHint);
      this.book.appendChild(this.bookInner);
      this.root.appendChild(this.frame);
      // Außerhalb des transformierten #hud-photo-Elternknotens: position:fixed
      // deckt so zuverlässig den ganzen Viewport ab.
      document.body.appendChild(this.book);
      this._onBookBackdrop = (event) => {
        if (event.target === this.book) this.setBookOpen(false, ctx, false);
      };
      this.book.addEventListener('click', this._onBookBackdrop);
    }

    // Im offenen Buch blockiert Input absichtlich Spieltasten. B/Escape müssen
    // deshalb wie beim Hilfe-Overlay direkt am Fenster behandelt werden.
    this._onBookKey = (event) => {
      if (!this.bookOpen) return;
      if (event.code === 'KeyB' || event.code === 'Escape') {
        event.preventDefault();
        this.setBookOpen(false, ctx, event.code === 'KeyB');
      }
    };
    window.addEventListener('keydown', this._onBookKey);

    // Capture nach dem Render dieses Frames (Framebuffer ist dann frisch).
    this._onFrameEnd = () => {
      if (!this._pending) return;
      this._pending = false;
      this._capture(this._capCtx);
    };
  }

  update(dt, ctx) {
    if (this._showT > 0) {
      this._showT -= dt;
      if (this._showT <= 0) this._hide();
    }

    if (!ctx.started) return;
    const input = ctx.input;
    if (!input || this._busy) return;

    if (input.justPressed?.('logbook')) {
      this.setBookOpen(!this.bookOpen, ctx);
      return;
    }
    if (this.bookOpen) return;

    // C-Kollision: Control ist Ducken; bloßes C fotografiert.
    if (!input.keyPressed('KeyC')) return;
    if (input.key('ControlLeft') || input.key('ControlRight')) return;

    this._busy = true;
    this._capCtx = ctx;
    this._pending = true;
    requestAnimationFrame(this._onFrameEnd);
  }

  lateUpdate() {
    // Capture hängt am rAF nach render().
  }

  _capture(ctx) {
    try {
      const renderer = ctx.renderer;
      if (!renderer?.domElement) return;
      // 0,72 JPEG + 30er-Limit: localStorage bleibt klein. Älteste Bilder
      // weichen zuerst — reine Chronik, kein materieller Season-Fortschritt.
      const dataUrl = renderer.domElement.toDataURL('image/jpeg', 0.72);
      const ws = ctx.worldSeconds ?? 0;
      const entry = {
        ws,
        clock: formatClock(ws, false),
        season: ctx.net?.seasonId ?? SEASON_ID,
        dataUrl,
      };
      this.log = appendPhotoLog(this.log, entry, MAX_PHOTOS);
      savePhotoLog(this.log);
      this._show(entry);
      ctx.systems?.audio?.shutter?.();
    } catch (err) {
      console.warn('[photo] Capture fehlgeschlagen:', err);
    } finally {
      this._busy = false;
      this._capCtx = null;
    }
  }

  setBookOpen(open, ctx, relock = true) {
    this.bookOpen = !!open;
    this.book?.classList.toggle('hidden', !this.bookOpen);
    const input = ctx?.input;
    if (this.bookOpen) {
      this._renderBook();
      input?.releaseLock?.();
      if (input) input.textCapture = true;
    } else if (input) {
      input.textCapture = false;
      if (relock) input.requestLock?.();
    }
  }

  _renderBook() {
    if (!this.bookGrid) return;
    this.bookGrid.textContent = '';
    if (this.log.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'photo-book-empty';
      empty.textContent = 'noch nichts festgehalten';
      this.bookGrid.appendChild(empty);
      return;
    }
    for (const entry of this.log) {
      const card = document.createElement('figure');
      card.className = 'photo-book-card';
      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      img.src = entry.dataUrl;
      const cap = document.createElement('figcaption');
      cap.textContent = `${entry.season} · ${entry.clock}`;
      card.append(img, cap);
      this.bookGrid.appendChild(card);
    }
  }

  _show(entry) {
    if (!this.frame || !this.img) return;
    this.img.src = entry.dataUrl;
    this.stamp.textContent = `${entry.season} · ${entry.clock}`;
    this.frame.style.display = 'block';
    this._showT = SHOW_S;
  }

  _hide() {
    if (this.frame) this.frame.style.display = 'none';
    this._showT = 0;
  }

  dispose() {
    this._hide();
    if (this._onBookKey) window.removeEventListener('keydown', this._onBookKey);
    if (this._onBookBackdrop) this.book?.removeEventListener('click', this._onBookBackdrop);
    this.book?.remove();
  }
}
